import {
  getFilteredOptions,
  hasSelectedOptions,
  recursivelyClearFilter,
  recursivelyToggleFilter,
  selectIfSelectedAllSubOptions,
  updateAllSelectableSubOptions,
} from "@faire/deprecated-retailer-visitor-shared/components/Filters/FilterUtils";
import { useFilterStore } from "@faire/deprecated-retailer-visitor-shared/components/Filters/useFilterStore";
import { Core } from "@faire/design-tokens";
import { IFilterOption } from "@faire/web-api--source/indigofair/data/IFilterOption";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { Button } from "@faire/web/slate/Button";
import Checkbox from "@faire/web/slate/Checkbox";
import { Chevron } from "@faire/web/slate/icons/Chevron";
import { Row } from "@faire/web/slate/Layout";
import Radio from "@faire/web/slate/Radio";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Span, Typography } from "@faire/web/slate/Typography";
import React, { useState, useCallback, type JSX } from "react";
import styled from "styled-components";

interface IProps {
  isSubOption: boolean;
  section: IFilterSection;
  option: IFilterOption;
  searchValue: string;
  onChange: (
    section: IFilterSection,
    filterKey: string,
    value: boolean
  ) => void;
  renderOption: (
    option: IFilterOption,
    isSubOption: boolean
  ) => JSX.Element | null;
  className?: string;
  onExpand?: (options: IFilterOption) => void;
  showUnformattedResultsCount?: boolean;
}

export const FilterOption: React.FC<IProps> = ({
  isSubOption,
  option,
  section,
  searchValue,
  renderOption,
  onChange,
  onExpand,
  showUnformattedResultsCount = false,
}) => {
  "use no memo"; // see comments on line 86 and 151

  const renderSubOptions = useCallback(
    (options: IFilterOption[]) => {
      if (options.length === 0) {
        return null;
      }

      return (
        <SuboptionsRow>
          <Spacer size="2x" />
          <OptionsWrapper>
            {options.map((option) => renderOption(option, true))}
          </OptionsWrapper>
        </SuboptionsRow>
      );
    },
    [renderOption]
  );

  /** View comment at line 151 */
  const selected = !!option.is_selected;

  const disabled = option.results_count === 0 && !selected;
  const intermediate =
    section.selection_type === IFilterSection.SelectionType.MULTIPLE &&
    !selected &&
    hasSelectedOptions(option);
  const singleSelection =
    section.selection_type === IFilterSection.SelectionType.SINGLE ||
    section.selection_type === IFilterSection.SelectionType.SINGLE_REQUIRED;
  const filteredSubOptions = React.useMemo(
    () => getFilteredOptions(option.sub_options, searchValue),
    [option.sub_options, searchValue]
  );

  const { strictLocalize } = useStrictLocalization();
  const {
    data: { hideOptionsResultCount },
  } = useFilterStore(["hideOptionsResultCount"]);

  const [collapsed, setCollapsed] = useState(!intermediate);

  const showSubOptions = !collapsed || searchValue !== "";

  const handleHeaderClick = useCallback(() => {
    if (collapsed) {
      onExpand?.(option);
    }
    setCollapsed(!collapsed);
  }, [collapsed, onExpand, option]);

  const toggleFilterOption = useCallback(() => {
    if (disabled) {
      return;
    }

    if (singleSelection) {
      section.options?.forEach((thisOption) => {
        recursivelyClearFilter(thisOption, option.key);
      });
    }

    const isRequired =
      section.selection_type === IFilterSection.SelectionType.SINGLE_REQUIRED;
    if (isRequired) {
      section.options?.forEach((thisOption) => {
        recursivelyToggleFilter(thisOption, option.key ?? "", true);
      });
    } else {
      const toggle = !option.is_selected;
      section.options?.forEach((thisOption) => {
        recursivelyToggleFilter(thisOption, option.key ?? "", toggle);
      });
    }

    if (!singleSelection) {
      updateAllSelectableSubOptions(option);
      section.options.forEach(selectIfSelectedAllSubOptions);
    }

    onChange(section, option.key ?? "", option.is_selected ?? false);
  }, [disabled, onChange, option, section, singleSelection]);

  //TODO(Juliette.li@faire): either ask BE to add formatted_results_count or create one for logged-out category pages.
  const renderLabel = () => {
    return (
      <FilterOptionText id={`filter-${option.key}`}>
        <FilterOptionTextSpan
          $disabled={disabled}
          data-show-result-count={
            !!(option.formatted_results_count && !hideOptionsResultCount)
          }
          color={disabled ? Core.text.subdued : Core.text.default}
          aria-label={option.display_name}
          title={option.display_name}
        >
          {option.display_name}
        </FilterOptionTextSpan>
        {option.formatted_results_count && !hideOptionsResultCount ? (
          <ResultCountText color={Core.text.subdued}>
            {showUnformattedResultsCount && !!option.results_count
              ? option.results_count
              : option.formatted_results_count}
          </ResultCountText>
        ) : null}
      </FilterOptionText>
    );
  };

  const renderSelectionType = () => {
    const isNotEcoFriendlyOption = !(
      !isSubOption &&
      section.field_name ===
        IFilterSection.FieldName.ECO_FRIENDLY_PRODUCT_ATTRIBUTES
    );

    if (singleSelection) {
      return (
        <Radio
          name={option.key}
          active={selected}
          disabled={disabled}
          onClick={toggleFilterOption}
          align="flex-start"
        >
          {renderLabel()}
        </Radio>
      );
    } else if (isNotEcoFriendlyOption) {
      return (
        <Checkbox
          checked={selected}
          intermediate={intermediate}
          disabled={disabled}
          onChange={toggleFilterOption}
          align="flex-start"
        >
          <CBFixWrapper>{renderLabel()}</CBFixWrapper>
        </Checkbox>
      );
    }

    return <CBFixWrapper>{renderLabel()}</CBFixWrapper>;
  };

  if (!option.key) {
    return null;
  }

  return (
    <React.Fragment>
      <OptionRow
        $disabled={disabled}
        $selected={!!option.is_selected || intermediate}
        $twoColumnLayout={false}
      >
        {renderSelectionType()}
        {!disabled && filteredSubOptions.length > 0 ? (
          <ChevronWrapper
            onClick={handleHeaderClick}
            aria-label={
              collapsed
                ? strictLocalize(
                    {
                      id: "xFOdil",
                      defaultMessage: "Expand {filterNameToken} filter",
                      description: {
                        text: "Aria label for expand filter option",
                        img: "https://cdn.faire.com/fastly/d9ff7f62068099d2ea7051eea3a53afca185cf9720d6ff116b0ab2abe352dcbe.png",
                      },
                    },
                    {
                      filterNameToken: option.display_name ?? option.key,
                    }
                  )
                : strictLocalize(
                    {
                      id: "BqeQbg",
                      defaultMessage: "Collapse {filterNameToken} filter",
                      description: {
                        text: "Aria label for collapse filter option",
                        img: "https://cdn.faire.com/fastly/d9ff7f62068099d2ea7051eea3a53afca185cf9720d6ff116b0ab2abe352dcbe.png",
                      },
                    },
                    {
                      filterNameToken: option.display_name ?? option.key,
                    }
                  )
            }
          >
            <StyledChevron
              direction={showSubOptions ? "up" : "down"}
              color={Core.icon.default}
              width="12px"
            />
          </ChevronWrapper>
        ) : null}
      </OptionRow>
      {showSubOptions ? renderSubOptions(filteredSubOptions) : null}
    </React.Fragment>
  );
};

const ChevronWrapper = styled(Button).attrs({
  variant: "tertiary",
})`
  flex-grow: 1;
  justify-content: flex-end;
`;

const StyledChevron = styled(Chevron)`
  float: right;
  margin-top: 2px;
`;

const FilterOptionTextSpan = styled(Span)<{
  $disabled: boolean;
}>`
  word-break: break-word;
  -ms-word-break: break-word;
  cursor: pointer;
  &[title]:hover {
    cursor: pointer;
  }
  &:hover {
    color: ${(props) =>
      props.$disabled ? Core.text.subdued : Core.text.hover};
    cursor: pointer;
  }

  &[data-show-result-count="false"] {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const FilterOptionText = styled(Typography)`
  float: left;
`;

const ResultCountText = styled(Span)`
  &&& {
    margin-left: ${getSpacing("1x")};
  }
`;

const OptionOrSeeMoreRow = styled(Row).attrs({ align: "flex-start" })`
  position: relative;
  width: 100%;
  padding-bottom: ${getSpacing("1x")};
`;

const OptionRow = styled(OptionOrSeeMoreRow)<{
  $disabled: boolean;
  $selected: boolean;
  $twoColumnLayout?: boolean;
}>`
  width: ${({ $twoColumnLayout: twoColumnLayout }) =>
    twoColumnLayout ? "50%" : "100%"};
  cursor: ${({ $disabled: disabled }) => (disabled ? "default" : "pointer")};

  .radio {
    border-color: ${({ $disabled: disabled }) =>
      disabled ? Core.border.subdued : Core.border.default};
  }

  &:hover {
    .radio {
      border-color: ${({ $disabled: disabled }) =>
        disabled ? Core.border.subdued : Core.border.hover};
    }

    ${FilterOptionText} {
      color: ${({ $disabled: disabled }) =>
        disabled ? Core.text.subdued : Core.text.enabled};
    }
  }
`;

const CBFixWrapper = styled.div`
  margin-top: -2px;
`;

const SuboptionsRow = styled(Row)`
  width: 100%;
`;

const OptionsWrapper = styled(Row).attrs({ align: "center" })`
  position: relative;
  width: 100%;
  flex-wrap: wrap;
`;
